/**
 * "We've updated our privacy policy" — shown once to a signed-in user whose last acknowledged
 * version is older than the current one (owner, 15 Oct 2026: keep it to the one sentence). The
 * server already knew both versions; nothing read them until this. Pressing the button records the
 * acknowledgement, and the notice does not come back. A new sign-up accepts the current version at
 * sign-up and never sees it.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { acceptTerms, getTermsStatus, type TermsStatusResponse } from '../../services/termsApi.ts';
import { PRIVACY_POLICY } from '../../constants/legal/index.ts';
import LegalDocModal from './LegalDocModal.tsx';
import { Button } from '../ui/index.tsx';

export const PRIVACY_UPDATE_HEADING = "We've updated our privacy policy";

/**
 * True when the caller acknowledged an EARLIER version of the privacy policy. Someone with no
 * acknowledgement on record is not asked: a brand-new account records its acceptance in the moments
 * after sign-up, and asking in that window put a box over the first screen before the record had
 * landed (the browser flows caught it, 16 Oct 2026). The policy's promise is to those who agreed to
 * an older version, and that is exactly who this asks.
 */
export function privacyPolicyNeedsAcknowledgement(status: TermsStatusResponse | null | undefined): boolean {
    const current = status?.current?.privacy_policy;
    const accepted = status?.accepted?.privacy_policy?.version;
    if (!current || !accepted) return false;
    return accepted !== current;
}

interface Props {
    /** Something that changes when the signed-in user does (their id), null when nobody is signed in. */
    userKey: string | null;
}

const PrivacyPolicyUpdateNotice = ({ userKey }: Props) => {
    const [show, setShow] = useState(false);
    const [reading, setReading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let live = true;
        if (!userKey) { setShow(false); return undefined; }
        // A build without accounts, a lapsed session or an unreachable server: no notice, no noise.
        getTermsStatus()
            .then((status) => { if (live) setShow(privacyPolicyNeedsAcknowledgement(status)); })
            .catch(() => { if (live) setShow(false); });
        return () => { live = false; };
    }, [userKey]);

    if (!show || typeof document === 'undefined') return null;

    const acknowledge = async () => {
        setSaving(true);
        try {
            await acceptTerms(['privacy_policy'], 'reacceptance');
            setShow(false);
        } catch {
            // Leave the notice up; the next sign-in asks again.
            setSaving(false);
        }
    };

    return createPortal(
        <>
            <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-labelledby="privacy-update-heading" data-testid="privacy-policy-update-notice">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                    <h2 id="privacy-update-heading" className="text-lg font-bold text-slate-900">{PRIVACY_UPDATE_HEADING}</h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                        Please take a moment to read the updated policy (version {PRIVACY_POLICY.effectiveDate}).
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2 justify-end">
                        <Button variant="secondary" size="sm" onClick={() => setReading(true)}>Read the policy</Button>
                        <Button variant="brand" size="sm" onClick={acknowledge} disabled={saving}>{saving ? 'Saving…' : "I've read it"}</Button>
                    </div>
                </div>
            </div>
            {reading && <LegalDocModal doc={PRIVACY_POLICY} onClose={() => setReading(false)} />}
        </>,
        document.body,
    );
};

export default PrivacyPolicyUpdateNotice;
